import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';

import { CountdownTimerComponent } from './countdown-timer.component';
import { getPropsConfig } from './countdown-timer.props-config';
import { COUNTDOWN_DEFAULTS, type CountdownTimerPropsModel } from './countdown-timer.props-model';
import { countdownTimerV2PaddingToMarginMigration } from './migrations/002-countdown-timer-padding-to-margin.migration';

export type CountdownTimerDef = WithProps<CountdownTimerPropsModel> &
  WithIoConfig<'value' | 'visible', 'action'>;
export type CountdownTimerElement = CanvasElementFrom<'countdownTimer'>;

declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    countdownTimer: CountdownTimerDef;
  }
}

registerElementDefinition({
  type: 'countdownTimer',
  componentType: CountdownTimerComponent,
  isListed: true,
  name: 'Countdown',
  width: { mode: 'value', value: COUNTDOWN_DEFAULTS.DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: COUNTDOWN_DEFAULTS.DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
    paths: {},
  },
  version: 2,
  migrations: {
    2: countdownTimerV2PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig<'value' | 'visible', 'action'> {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'Value', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'action',
        schema: DEFAULT_SCHEMA,
      },
    ],
  };
}

function getDefaults(): PropsOf<'countdownTimer'> {
  return {
    content: {
      title: {
        label: 'Countdown Timer',
        position: 'top',
        align: 'left',
        width: '35%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      data: {
        targetField: '',
      },
      options: {
        template: 'block',
        duration: '30min',
        show: ['days', 'hrs', 'min', 's'],
        labelsPosition: 'bottom',
        showDividers: true,
      },
    },
    style: {
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
        fontFamily: '$t.fontFamily',
        fontSize: '20px',
        fontStyle: '$t.headerFontStyle' as any,
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
        lineHeight: '$t.lineHeight',
      },
      day: {
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        fontSize: '32px',
        fontStyle: '$t.headerFontStyle' as any,
        value: { color1: '$t.complementary', selectorType: 'solid' },
        fill: { color1: '$t.title', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'none' },
        radius: '$t.radius',
        borderWidth: '1px',
      },
      time: {
        fill: { color1: '$t.complementary', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'none' },
        radius: '$t.radius',
        borderWidth: '1px',
        divider: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        dividerWidth: '1px',
        textPart: 'value',
        value: {
          fontFamily: '$t.headerFontFamily',
          fontWeight: '$t.headerFontWeight',
          fontSize: '40px',
          fontStyle: '$t.headerFontStyle' as any,
          value: { color1: '$t.title', selectorType: 'solid' },
        },
        label: {
          fontFamily: '$t.headerFontFamily',
          fontWeight: '$t.headerFontWeight',
          fontSize: '13px',
          fontStyle: '$t.headerFontStyle' as any,
          value: { color1: '$t.value', selectorType: 'solid' },
        },
      },
      label: {
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        fontSize: '32px',
        fontStyle: '$t.headerFontStyle' as any,
        value: { color1: '$t.title', selectorType: 'solid' },
        divider: { color1: '$t.value_a70', selectorType: 'solid' },
      },
      clock: {
        gutter: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        active: { color1: '$t.title', selectorType: 'solid' },
        width: '15px',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'countdownTimer'> {
  return {
    content: {},
  };
}
